import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  AccountTree as AccountTreeIcon,
  BugReport as BugReportIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { get } from '../../services/apiService';
import OnboardingTab from './OnboardingTab';

// Dark green color matching the theme
const darkGreenColor = "#1D4434";

/**
 * RCA Tab Component
 * @returns {JSX.Element} RCA tab content
 */
const RCATab = () => {
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  
  // List state
  const [loadingItems, setLoadingItems] = useState(true);
  const [rcaItems, setRcaItems] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [dueDateFilter, setDueDateFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [adoConfig, setAdoConfig] = useState({ orgUrl: '', project: '' });
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Actions modal state
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [selectedRcaItem, setSelectedRcaItem] = useState(null);
  const [actions, setActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  
  // Modal state for metric details
  const [metricModalOpen, setMetricModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [modalItems, setModalItems] = useState([]);
  const [loadingModalItems, setLoadingModalItems] = useState(false);

  // Fetch RCA metrics
  const fetchMetrics = async () => {
    try {
      setLoadingMetrics(true);
      setError(null);
      const response = await get('/api/engineering-processes/rca/metrics');
      
      if (response.success) {
        setMetrics(response.metrics);
      } else {
        setError('Failed to fetch RCA metrics');
      }
    } catch (err) {
      console.error('Error fetching RCA metrics:', err);
      setError('Failed to load RCA metrics');
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  // Fetch RCA items list
  const fetchRCAItems = async () => {
    try {
      setLoadingItems(true);
      const response = await get('/api/engineering-processes/rca/items', {
        page: page + 1,
        limit: rowsPerPage,
        state: statusFilter || undefined,
        team: teamFilter || undefined,
        dueDateFilter: dueDateFilter !== 'all' ? dueDateFilter : undefined,
        sortBy: sortBy,
        sortOrder: sortOrder
      });
      
      if (response.success) {
        setRcaItems(response.data || []);
        setTotalItems(response.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching RCA items:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  // Fetch available statuses
  const fetchStatuses = async () => {
    try {
      const response = await get('/api/engineering-processes/rca/statuses');
      if (response.success) {
        setAvailableStatuses(response.statuses || []);
      }
    } catch (err) {
      console.error('Error fetching statuses:', err);
    }
  };

  // Fetch available teams from RCA items
  const fetchTeams = async () => {
    try {
      const response = await get('/api/engineering-processes/rca/teams');
      if (response.success) {
        setAvailableTeams(response.teams || []);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  // Fetch Azure DevOps configuration for building work item URLs
  const fetchAdoConfig = async () => {
    try {
      const response = await get('/api/engineering-processes/ado-config');
      if (response.success) {
        setAdoConfig({
          orgUrl: response.orgUrl || 'https://dev.azure.com',
          project: response.project || 'HighQ Tech'
        });
      }
    } catch (err) {
      console.error('Error fetching ADO config:', err);
    }
  };

  // Fetch actions for an RCA item
  const fetchActions = async (rcaWorkItemId) => {
    try {
      setLoadingActions(true);
      const response = await get(`/api/engineering-processes/rca/actions/${rcaWorkItemId}`);
      if (response.success) {
        setActions(response.actions || []);
      }
    } catch (err) {
      console.error('Error fetching actions:', err);
      setActions([]);
    } finally {
      setLoadingActions(false);
    }
  };

  // Handle opening actions modal
  const handleOpenActions = async (item) => {
    setSelectedRcaItem(item);
    setActionsModalOpen(true);
    await fetchActions(item.workItemId);
  };

  // Handle closing actions modal
  const handleCloseActions = () => {
    setActionsModalOpen(false);
    setSelectedRcaItem(null);
    setActions([]);
  };

  // Build work item URL
  const getWorkItemUrl = (workItemId, teamProject) => {
    const project = teamProject || adoConfig.project || 'HighQ Tech';
    const encodedProject = encodeURIComponent(project);
    return `${adoConfig.orgUrl}/${encodedProject}/_workitems/edit/${workItemId}/`;
  };

  useEffect(() => {
    fetchMetrics();
    fetchStatuses();
    fetchTeams();
    fetchAdoConfig();
  }, []);

  useEffect(() => {
    fetchRCAItems();
  }, [page, rowsPerPage, statusFilter, teamFilter, dueDateFilter, sortBy, sortOrder]);

  // Check if item is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Format last updated date
  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleTeamFilterChange = (event) => {
    setTeamFilter(event.target.value);
    setPage(0);
  };

  const handleDueDateFilterChange = (event) => {
    setDueDateFilter(event.target.value);
    setPage(0);
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return '#9e9e9e';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('closed') || statusLower.includes('resolved') || statusLower.includes('done') || statusLower.includes('completed')) {
      return '#4caf50'; // Green
    } else if (statusLower.includes('active') || statusLower.includes('in progress') || statusLower.includes('new')) {
      return '#2196f3'; // Blue
    } else if (statusLower.includes('blocked') || statusLower.includes('waiting')) {
      return '#ff9800'; // Orange
    } else {
      return '#9e9e9e'; // Gray
    }
  };

  // Handle card click
  const handleCardClick = async (metricType, metricData) => {
    setSelectedMetric({ type: metricType, data: metricData });
    setMetricModalOpen(true);
    setLoadingModalItems(true);
    setModalItems([]);
    
    try {
      // Build filter based on metric type
      let queryParams = { page: 1, limit: 50 };
      
      if (metricType === 'active') {
        // Active items are not closed
        queryParams.state = 'active';
      } else if (metricType === 'closed') {
        queryParams.state = 'closed';
      } else if (metricType === 'overdue') {
        queryParams.dueDateFilter = 'overdue';
      } else if (metricType === 'upcoming') {
        queryParams.dueDateFilter = 'upcoming';
      }
      
      const response = await get('/api/engineering-processes/rca/items', queryParams);
      
      if (response.success) {
        setModalItems(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching modal items:', err);
    } finally {
      setLoadingModalItems(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setMetricModalOpen(false);
    setSelectedMetric(null);
    setModalItems([]);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h5" sx={{ color: darkGreenColor, fontWeight: 'bold' }}>
          Root Cause Analysis (RCA)
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
          {metrics?.lastSync && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Last updated: {formatLastUpdated(metrics.lastSync)}
            </Typography>
          )}
          {metrics?.lastSync && metrics?.syncIntervalHours && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Next update: {formatLastUpdated(
                new Date(new Date(metrics.lastSync).getTime() + (metrics.syncIntervalHours * 60 * 60 * 1000))
              )}
            </Typography>
          )}
        </Box>
      </Box>

      {loadingMetrics ? (
        <Box sx={{ mb: 4 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            Loading RCA metrics...
          </Typography>
        </Box>
      ) : error ? (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0', mb: 3 }}>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Summary Metrics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Total RCA Items Card */}
            <Grid item xs={6} sm={4} md={2}>
              <Card
                onClick={() => handleCardClick('total', { count: metrics?.totalItems || 0, label: 'Total RCA Items' })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Total RCA Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.totalItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      All RCA items
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Active Items Card */}
            <Grid item xs={6} sm={4} md={2}>
              <Card
                onClick={() => handleCardClick('active', { count: metrics?.activeItems || 0, label: 'Active Items', percentage: metrics?.totalItems > 0 ? Math.round((metrics.activeItems / metrics.totalItems) * 100) : 0 })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Active Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.activeItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      {metrics?.totalItems > 0 ? Math.round((metrics.activeItems / metrics.totalItems) * 100) : 0}% of total
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Closed Items Card */}
            <Grid item xs={6} sm={4} md={2}>
              <Card
                onClick={() => handleCardClick('closed', { count: metrics?.closedItems || 0, label: 'Closed Items', percentage: metrics?.totalItems > 0 ? Math.round((metrics.closedItems / metrics.totalItems) * 100) : 0 })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Closed Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.closedItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      {metrics?.totalItems > 0 ? Math.round((metrics.closedItems / metrics.totalItems) * 100) : 0}% resolved
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Overdue Items Card */}
            <Grid item xs={6} sm={4} md={2}>
              <Card
                onClick={() => handleCardClick('overdue', { count: metrics?.overdueItems || 0, label: 'Overdue Items', percentage: metrics?.itemsWithDueDate > 0 ? Math.round((metrics.overdueItems / metrics.itemsWithDueDate) * 100) : 0 })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Overdue Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.overdueItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      {metrics?.itemsWithDueDate > 0 ? Math.round((metrics.overdueItems / metrics.itemsWithDueDate) * 100) : 0}% overdue
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <WarningIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Upcoming Items Card */}
            <Grid item xs={6} sm={4} md={2}>
              <Card
                onClick={() => handleCardClick('upcoming', { count: metrics?.upcomingItems || 0, label: 'Upcoming Items' })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Upcoming Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.upcomingItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      Due within 7 days
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Metric Details Modal */}
          <Dialog
            open={metricModalOpen}
            onClose={handleCloseModal}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {selectedMetric?.data?.label || 'Metric Details'}
                </Typography>
                <IconButton onClick={handleCloseModal} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {loadingModalItems ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                    {selectedMetric?.data?.count || 0} Items
                  </Typography>
                  {selectedMetric?.data?.percentage !== undefined && (
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      {selectedMetric.data.percentage}% of total
                    </Typography>
                  )}
                  
                  {modalItems.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Work Item ID</strong></TableCell>
                            <TableCell><strong>Title</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Assigned To</strong></TableCell>
                            <TableCell><strong>Team</strong></TableCell>
                            <TableCell><strong>Due Date</strong></TableCell>
                            <TableCell><strong>Created Date</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modalItems.map((item, index) => (
                            <TableRow key={item._id || index}>
                              <TableCell>
                                <Link
                                  href={getWorkItemUrl(item.workItemId, item.teamProject)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    textDecoration: 'none',
                                    color: '#1976d2',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  {item.workItemId}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                  {item.title || 'Untitled'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.state || 'Unknown'}
                                  size="small"
                                  sx={{
                                    bgcolor: getStatusColor(item.state),
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              </TableCell>
                              <TableCell>{item.assignedTo || 'Unassigned'}</TableCell>
                              <TableCell>{item.team || 'N/A'}</TableCell>
                              <TableCell>{item.dueDate ? formatDate(item.dueDate) : 'N/A'}</TableCell>
                              <TableCell>{formatDate(item.createdDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                      No items found for this metric.
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* RCA Items List */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ color: darkGreenColor, fontWeight: 'bold' }}>
                RCA Items ({totalItems})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Team</InputLabel>
                  <Select
                    value={teamFilter}
                    label="Team"
                    onChange={handleTeamFilterChange}
                  >
                    <MenuItem value="">
                      <em>All Teams</em>
                    </MenuItem>
                    {availableTeams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.displayName || team.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Due Date</InputLabel>
                  <Select
                    value={dueDateFilter}
                    label="Due Date"
                    onChange={handleDueDateFilterChange}
                  >
                    <MenuItem value="all">All Items</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="upcoming">Upcoming (7 days)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="">
                      <em>All Statuses</em>
                    </MenuItem>
                    {availableStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {loadingItems ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <LinearProgress sx={{ width: '100%' }} />
              </Box>
            ) : rcaItems.length > 0 ? (
              <>
                <TableContainer 
                  sx={{ 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    '&::-webkit-scrollbar': {
                      height: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#bdbdbd',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#9e9e9e'
                      }
                    }
                  }}
                >
                  <Table 
                    sx={{
                      minWidth: 1200,
                      '& .MuiTableCell-root': {
                        padding: '12px 16px',
                        fontSize: '0.875rem',
                        whiteSpace: 'nowrap'
                      },
                      '& .MuiTableHead-root': {
                        backgroundColor: '#f5f5f5',
                        '& .MuiTableCell-root': {
                          fontWeight: 600,
                          color: '#424242',
                          borderBottom: '2px solid #e0e0e0',
                          padding: '14px 16px',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          backgroundColor: '#f5f5f5'
                        }
                      },
                      '& .MuiTableBody-root': {
                        '& .MuiTableRow-root': {
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: '#f9f9f9',
                            transform: 'scale(1.001)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }
                        }
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ width: 100, minWidth: 100, cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('workItemId')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Work Item ID</strong>
                            {sortBy === 'workItemId' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ minWidth: 300, cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('title')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Title</strong>
                            {sortBy === 'title' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ width: 150, minWidth: 150, cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('team')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Team</strong>
                            {sortBy === 'team' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ width: 120, minWidth: 120, cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('state')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Status</strong>
                            {sortBy === 'state' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: 120, minWidth: 120 }}><strong>Actions</strong></TableCell>
                        <TableCell 
                          sx={{ width: 180, minWidth: 180, cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('assignedTo')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Assigned To</strong>
                            {sortBy === 'assignedTo' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ width: 130, minWidth: 130, cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('createdDate')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Created Date</strong>
                            {sortBy === 'createdDate' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ width: 130, minWidth: 130, cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('dueDate')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Due Date</strong>
                            {sortBy === 'dueDate' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rcaItems.map((item, index) => {
                        const overdue = isOverdue(item.dueDate) && item.state && !['Closed', 'Resolved', 'Done', 'Completed'].includes(item.state);
                        return (
                          <TableRow 
                            key={item._id || index}
                            sx={{
                              bgcolor: overdue ? 'rgba(244, 67, 54, 0.08)' : 'inherit',
                              borderLeft: overdue ? '4px solid #d32f2f' : '4px solid transparent',
                              '&:hover': {
                                bgcolor: overdue ? 'rgba(244, 67, 54, 0.12)' : '#f9f9f9'
                              }
                            }}
                          >
                            <TableCell>
                              <Link 
                                href={getWorkItemUrl(item.workItemId, item.teamProject)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  textDecoration: 'none', 
                                  color: overdue ? '#d32f2f' : '#1976d2',
                                  fontWeight: overdue ? 600 : 500,
                                  fontSize: '0.875rem',
                                  '&:hover': { 
                                    textDecoration: 'underline',
                                    color: overdue ? '#b71c1c' : '#1565c0'
                                  }
                                }}
                              >
                                {item.workItemId}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: overdue ? 600 : 400,
                                  color: '#212121',
                                  lineHeight: 1.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  whiteSpace: 'normal'
                                }}
                              >
                                {item.title || 'Untitled'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: overdue ? 600 : 400,
                                  color: '#616161',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {item.team || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={item.state || 'Unknown'} 
                                size="small"
                                sx={{ 
                                  bgcolor: getStatusColor(item.state),
                                  color: 'white',
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                  height: 24,
                                  borderRadius: 1
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {item.actionCounts && item.actionCounts.total > 0 ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleOpenActions(item)}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: darkGreenColor,
                                    color: darkGreenColor,
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    padding: '4px 12px',
                                    minWidth: 'auto',
                                    borderRadius: 1,
                                    '&:hover': {
                                      borderColor: darkGreenColor,
                                      bgcolor: `${darkGreenColor}15`,
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }
                                  }}
                                >
                                  {item.actionCounts.completed} / {item.actionCounts.total}
                                </Button>
                              ) : (
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  No actions
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: overdue ? 600 : 400,
                                  color: '#616161',
                                  fontSize: '0.875rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {item.assignedTo || 'Unassigned'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#616161',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {formatDate(item.createdDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {item.dueDate ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: overdue ? '#d32f2f' : '#616161',
                                      fontWeight: overdue ? 600 : 400,
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    {formatDate(item.dueDate)}
                                  </Typography>
                                  {overdue && (
                                    <Chip 
                                      label="Overdue" 
                                      size="small" 
                                      sx={{ 
                                        bgcolor: '#d32f2f',
                                        color: 'white',
                                        fontSize: '0.65rem',
                                        height: 20,
                                        fontWeight: 600,
                                        borderRadius: 1
                                      }} 
                                    />
                                  )}
                                </Box>
                              ) : (
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalItems}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                No RCA items found. Sync items from Azure DevOps to see data here.
              </Typography>
            )}
          </Paper>
          
          {/* Actions Modal */}
          <Dialog
            open={actionsModalOpen}
            onClose={handleCloseActions}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Actions for RCA #{selectedRcaItem?.workItemId} - {selectedRcaItem?.title}
                </Typography>
                <IconButton onClick={handleCloseActions} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              {loadingActions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <LinearProgress sx={{ width: '100%' }} />
                </Box>
              ) : actions.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Work Item ID</strong></TableCell>
                        <TableCell><strong>Title</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Team</strong></TableCell>
                        <TableCell><strong>Assigned To</strong></TableCell>
                        <TableCell><strong>Due Date</strong></TableCell>
                        <TableCell><strong>Created Date</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {actions.map((action, index) => (
                        <TableRow key={action._id || index}>
                          <TableCell>
                            <Link
                              href={getWorkItemUrl(action.actionWorkItemId, action.teamProject || selectedRcaItem?.teamProject)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textDecoration: 'none', color: '#1976d2' }}
                            >
                              {action.actionWorkItemId}
                            </Link>
                          </TableCell>
                          <TableCell>{action.title || 'Untitled'}</TableCell>
                          <TableCell>{action.workItemType || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={action.state || 'Unknown'}
                              size="small"
                              sx={{
                                bgcolor: getStatusColor(action.state),
                                color: 'white'
                              }}
                            />
                          </TableCell>
                          <TableCell>{action.team || 'N/A'}</TableCell>
                          <TableCell>{action.assignedTo || 'Unassigned'}</TableCell>
                          <TableCell>
                            {action.dueDate ? formatDate(action.dueDate) : 'N/A'}
                          </TableCell>
                          <TableCell>{formatDate(action.createdDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                  No actions found for this RCA item.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseActions}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

/**
 * Technical Debt Tab Component
 * @returns {JSX.Element} Technical Debt tab content
 */
const TechnicalDebtTab = () => {
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  
  // List state
  const [loadingItems, setLoadingItems] = useState(true);
  const [technicalDebtItems, setTechnicalDebtItems] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [dueDateFilter, setDueDateFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [adoConfig, setAdoConfig] = useState({ orgUrl: '', project: '' });
  const [relatedItemsMap, setRelatedItemsMap] = useState({});
  const [loadingRelatedItems, setLoadingRelatedItems] = useState({});
  const [sortBy, setSortBy] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [overdueCount, setOverdueCount] = useState(0);
  
  // Modal state for metric details
  const [metricModalOpen, setMetricModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [modalItems, setModalItems] = useState([]);
  const [loadingModalItems, setLoadingModalItems] = useState(false);

  // Fetch Technical Debt metrics
  const fetchMetrics = async () => {
    try {
      setLoadingMetrics(true);
      setError(null);
      const response = await get('/api/engineering-processes/technical-debt/metrics');
      
      if (response.success) {
        setMetrics(response.metrics);
      } else {
        setError('Failed to fetch Technical Debt metrics');
      }
    } catch (err) {
      console.error('Error fetching Technical Debt metrics:', err);
      setError('Failed to load Technical Debt metrics');
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Fetch overdue count
  const fetchOverdueCount = async () => {
    try {
      const response = await get('/api/engineering-processes/technical-debt/items', {
        page: 1,
        limit: 1,
        dueDateFilter: 'overdue'
      });
      if (response.success) {
        setOverdueCount(response.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching overdue count:', err);
    }
  };

  // Fetch Technical Debt items list
  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  const fetchTechnicalDebtItems = async () => {
    try {
      setLoadingItems(true);
      const response = await get('/api/engineering-processes/technical-debt/items', {
        page: page + 1,
        limit: rowsPerPage,
        state: statusFilter || undefined,
        statusFilter: statusFilter || undefined,
        team: teamFilter || undefined,
        dueDateFilter: dueDateFilter || undefined,
        sortBy: sortBy,
        sortOrder: sortOrder
      });
      
      if (response.success) {
        setTechnicalDebtItems(response.data || []);
        setTotalItems(response.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching Technical Debt items:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  // Fetch available statuses
  const fetchStatuses = async () => {
    try {
      const response = await get('/api/engineering-processes/technical-debt/statuses');
      if (response.success) {
        setAvailableStatuses(response.statuses || []);
      }
    } catch (err) {
      console.error('Error fetching statuses:', err);
    }
  };

  // Fetch available teams
  const fetchTeams = async () => {
    try {
      const response = await get('/api/engineering-processes/technical-debt/teams');
      if (response.success) {
        setAvailableTeams(response.teams || []);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  // Fetch Azure DevOps configuration
  const fetchAdoConfig = async () => {
    try {
      const response = await get('/api/engineering-processes/ado-config');
      if (response.success) {
        setAdoConfig({
          orgUrl: response.orgUrl || 'https://dev.azure.com',
          project: response.project || 'HighQ Tech'
        });
      }
    } catch (err) {
      console.error('Error fetching ADO config:', err);
    }
  };

  // Build work item URL
  const getWorkItemUrl = (workItemId, teamProject) => {
    const project = teamProject || adoConfig.project || 'HighQ Tech';
    const encodedProject = encodeURIComponent(project);
    return `${adoConfig.orgUrl}/${encodedProject}/_workitems/edit/${workItemId}/`;
  };

  // Fetch related items for a work item
  const fetchRelatedItems = async (workItemId) => {
    if (relatedItemsMap[workItemId] !== undefined || loadingRelatedItems[workItemId]) {
      return; // Already fetched or currently loading
    }
    
    try {
      setLoadingRelatedItems(prev => ({ ...prev, [workItemId]: true }));
      const response = await get(`/api/engineering-processes/technical-debt/related-items/${workItemId}`);
      
      if (response.success) {
        setRelatedItemsMap(prev => ({
          ...prev,
          [workItemId]: response.relatedItems || []
        }));
      }
    } catch (err) {
      console.error(`Error fetching related items for ${workItemId}:`, err);
      setRelatedItemsMap(prev => ({
        ...prev,
        [workItemId]: []
      }));
    } finally {
      setLoadingRelatedItems(prev => {
        const newState = { ...prev };
        delete newState[workItemId];
        return newState;
      });
    }
  };

  useEffect(() => {
    fetchMetrics();
    fetchStatuses();
    fetchTeams();
    fetchAdoConfig();
    fetchOverdueCount();
  }, []);

  useEffect(() => {
    fetchTechnicalDebtItems();
    // Refresh overdue count when filters change
    if (dueDateFilter !== 'overdue') {
      fetchOverdueCount();
    }
  }, [page, rowsPerPage, statusFilter, teamFilter, dueDateFilter, sortBy, sortOrder]);

  // Fetch related items when items are loaded
  useEffect(() => {
    if (technicalDebtItems.length > 0) {
      technicalDebtItems.forEach(item => {
        if (item.workItemId && !relatedItemsMap[item.workItemId] && !loadingRelatedItems[item.workItemId]) {
          fetchRelatedItems(item.workItemId);
        }
      });
    }
  }, [technicalDebtItems]);

  // Check if item is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (err) {
      return 'Invalid date';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleTeamFilterChange = (event) => {
    setTeamFilter(event.target.value);
    setPage(0);
  };

  const handleDueDateFilterChange = (event) => {
    setDueDateFilter(event.target.value);
    setPage(0);
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return '#9e9e9e';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('closed') || statusLower.includes('resolved') || statusLower.includes('done') || statusLower.includes('completed')) {
      return '#4caf50';
    } else if (statusLower.includes('active') || statusLower.includes('in progress') || statusLower.includes('new')) {
      return '#2196f3';
    } else if (statusLower.includes('blocked') || statusLower.includes('waiting')) {
      return '#ff9800';
    } else {
      return '#9e9e9e';
    }
  };

  // Format last updated date
  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Handle card click
  const handleCardClick = async (metricType, metricData) => {
    setSelectedMetric({ type: metricType, data: metricData });
    setMetricModalOpen(true);
    setLoadingModalItems(true);
    setModalItems([]);
    
    try {
      // Build query parameters based on metric type
      let queryParams = { page: 1, limit: 50 };
      
      if (metricType === 'active') {
        // For active, we need to filter out closed states
        // The API will handle this, but we can use statusFilter
        queryParams.statusFilter = 'all'; // Will be filtered by backend to exclude closed
      } else if (metricType === 'closed') {
        // Closed items - need to check what states are considered closed
        queryParams.state = 'Closed';
      } else if (metricType === 'new') {
        queryParams.statusFilter = 'new';
      } else if (metricType === 'sendForReview') {
        queryParams.statusFilter = 'send-for-review';
      } else if (metricType === 'approved') {
        queryParams.statusFilter = 'approved';
      }
      
      const response = await get('/api/engineering-processes/technical-debt/items', queryParams);
      
      if (response.success) {
        // Filter results based on metric type if needed
        let items = response.data || [];
        
        if (metricType === 'active') {
          // Filter out closed/resolved/done/completed items
          items = items.filter(item => {
            const state = (item.state || '').toLowerCase();
            return !['closed', 'resolved', 'done', 'completed'].includes(state);
          });
        }
        
        setModalItems(items);
      }
    } catch (err) {
      console.error('Error fetching modal items:', err);
    } finally {
      setLoadingModalItems(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setMetricModalOpen(false);
    setSelectedMetric(null);
    setModalItems([]);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h5" sx={{ color: darkGreenColor, fontWeight: 'bold' }}>
          Technical Debt
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
          {metrics?.lastSync && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Last updated: {formatLastUpdated(metrics.lastSync)}
            </Typography>
          )}
          {metrics?.lastSync && metrics?.syncIntervalHours && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Next update: {formatLastUpdated(
                new Date(new Date(metrics.lastSync).getTime() + (metrics.syncIntervalHours * 60 * 60 * 1000))
              )}
            </Typography>
          )}
        </Box>
      </Box>

      {loadingMetrics ? (
        <Box sx={{ mb: 4 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            Loading Technical Debt metrics...
          </Typography>
        </Box>
      ) : error ? (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0', mb: 3 }}>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Summary Metrics Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {/* Total Technical Debt Items Card */}
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 0' }, minWidth: 0 }}>
              <Card
                onClick={() => handleCardClick('total', { count: metrics?.totalItems || 0, label: 'Total Technical Debt Items' })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Total Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.totalItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      All items
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <BugReportIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Active Items Card */}
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 0' }, minWidth: 0 }}>
              <Card
                onClick={() => handleCardClick('active', { count: metrics?.activeItems || 0, label: 'Active Items', percentage: metrics?.totalItems > 0 ? Math.round((metrics.activeItems / metrics.totalItems) * 100) : 0 })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Active Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.activeItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      {metrics?.totalItems > 0 ? Math.round((metrics.activeItems / metrics.totalItems) * 100) : 0}% of total
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Closed Items Card */}
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 0' }, minWidth: 0 }}>
              <Card
                onClick={() => handleCardClick('closed', { count: metrics?.closedItems || 0, label: 'Closed Items', percentage: metrics?.totalItems > 0 ? Math.round((metrics.closedItems / metrics.totalItems) * 100) : 0 })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Closed Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.closedItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      {metrics?.totalItems > 0 ? Math.round((metrics.closedItems / metrics.totalItems) * 100) : 0}% resolved
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* New Items Card */}
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 0' }, minWidth: 0 }}>
              <Card
                onClick={() => handleCardClick('new', { count: metrics?.newItems || 0, label: 'New Items', percentage: metrics?.totalItems > 0 ? Math.round((metrics.newItems / metrics.totalItems) * 100) : 0 })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      New Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.newItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      {metrics?.totalItems > 0 ? Math.round((metrics.newItems / metrics.totalItems) * 100) : 0}% of total
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <AccountTreeIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Send For Review Items Card */}
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 0' }, minWidth: 0 }}>
              <Card
                onClick={() => handleCardClick('sendForReview', { count: metrics?.sendForReviewItems || 0, label: 'Send For Review', percentage: metrics?.totalItems > 0 ? Math.round((metrics.sendForReviewItems / metrics.totalItems) * 100) : 0 })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Send For Review
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.sendForReviewItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      {metrics?.totalItems > 0 ? Math.round((metrics.sendForReviewItems / metrics.totalItems) * 100) : 0}% of total
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Approved Items Card */}
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 0' }, minWidth: 0 }}>
              <Card
                onClick={() => handleCardClick('approved', { count: metrics?.approvedItems || 0, label: 'Approved Items', percentage: metrics?.totalItems > 0 ? Math.round((metrics.approvedItems / metrics.totalItems) * 100) : 0 })}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #a5d6a7 0%, #81c784 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Approved Items
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {metrics?.approvedItems || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      {metrics?.totalItems > 0 ? Math.round((metrics.approvedItems / metrics.totalItems) * 100) : 0}% of total
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Overdue Items Card */}
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 0' }, minWidth: 0 }}>
              <Card
                onClick={() => {
                  setDueDateFilter('overdue');
                  setPage(0);
                }}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                  color: 'white',
                  boxShadow: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        opacity: 0.9,
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Overdue Reviews
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem'
                      }}
                    >
                      {overdueCount}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: '0.75rem'
                      }}
                    >
                      Past due date
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -15,
                      top: -15,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <WarningIcon sx={{ fontSize: 80, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Metric Details Modal */}
          <Dialog
            open={metricModalOpen}
            onClose={handleCloseModal}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {selectedMetric?.data?.label || 'Metric Details'}
                </Typography>
                <IconButton onClick={handleCloseModal} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {loadingModalItems ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                    {selectedMetric?.data?.count || 0} Items
                  </Typography>
                  {selectedMetric?.data?.percentage !== undefined && (
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      {selectedMetric.data.percentage}% of total
                    </Typography>
                  )}
                  
                  {modalItems.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Work Item ID</strong></TableCell>
                            <TableCell><strong>Title</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Assigned To</strong></TableCell>
                            <TableCell><strong>Created Date</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modalItems.map((item, index) => (
                            <TableRow key={item._id || index}>
                              <TableCell>
                                <Link
                                  href={getWorkItemUrl(item.workItemId, item.teamProject)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    textDecoration: 'none',
                                    color: '#1976d2',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  {item.workItemId}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                  {item.title || 'Untitled'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.state || 'Unknown'}
                                  size="small"
                                  sx={{
                                    bgcolor: getStatusColor(item.state),
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              </TableCell>
                              <TableCell>{item.assignedTo || 'Unassigned'}</TableCell>
                              <TableCell>{formatDate(item.createdDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                      No items found for this metric.
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Technical Debt Items List */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ color: darkGreenColor, fontWeight: 'bold' }}>
                Technical Debt Items ({totalItems})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="">
                      <em>All Statuses</em>
                    </MenuItem>
                    <MenuItem value="new">New</MenuItem>
                    <MenuItem value="send-for-review">Send For Review</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="new-review-approved">New, Send For Review, Approved</MenuItem>
                    {availableStatuses.filter(s => !['New', 'Send For Review', 'Approved'].includes(s)).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Team Filter</InputLabel>
                  <Select
                    value={teamFilter}
                    label="Team Filter"
                    onChange={handleTeamFilterChange}
                  >
                    <MenuItem value="">
                      <em>All Teams</em>
                    </MenuItem>
                    {availableTeams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.displayName || team.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Due Date Filter</InputLabel>
                  <Select
                    value={dueDateFilter}
                    label="Due Date Filter"
                    onChange={handleDueDateFilterChange}
                  >
                    <MenuItem value="all">All Items</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="upcoming">Upcoming (Next 7 Days)</MenuItem>
                    <MenuItem value="none">No Due Date</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {loadingItems ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <LinearProgress sx={{ width: '100%' }} />
              </Box>
            ) : technicalDebtItems.length > 0 ? (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('workItemId')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Work Item ID</strong>
                            {sortBy === 'workItemId' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('title')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Title</strong>
                            {sortBy === 'title' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('state')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Status</strong>
                            {sortBy === 'state' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('assignedTo')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Assigned To</strong>
                            {sortBy === 'assignedTo' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('team')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Team</strong>
                            {sortBy === 'team' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('createdDate')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Created Date</strong>
                            {sortBy === 'createdDate' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('dueDate')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>Due Date</strong>
                            {sortBy === 'dueDate' && (
                              sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell><strong>Relevant Linked Items</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {technicalDebtItems.map((item, index) => {
                        return (
                          <TableRow 
                            key={item._id || index}
                            sx={{
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <TableCell>
                              <Link 
                                href={getWorkItemUrl(item.workItemId, item.teamProject)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  textDecoration: 'none', 
                                  color: '#1976d2',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {item.workItemId}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {item.title || 'Untitled'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={item.state || 'Unknown'} 
                                size="small"
                                sx={{ 
                                  bgcolor: getStatusColor(item.state),
                                  color: 'white',
                                  fontWeight: '500'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {item.assignedTo || 'Unassigned'}
                            </TableCell>
                            <TableCell>
                              {item.team || 'N/A'}
                            </TableCell>
                            <TableCell>{formatDate(item.createdDate)}</TableCell>
                            <TableCell>
                              {item.dueDate ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: isOverdue(item.dueDate) && !['Closed', 'Removed', 'Done', 'Completed'].includes(item.state) 
                                        ? 'error.main' 
                                        : 'text.primary' 
                                    }}
                                  >
                                    {formatDate(item.dueDate)}
                                  </Typography>
                                  {isOverdue(item.dueDate) && !['Closed', 'Removed', 'Done', 'Completed'].includes(item.state) && (
                                    <Chip 
                                      label="Overdue" 
                                      size="small" 
                                      color="error"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">No due date</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {loadingRelatedItems[item.workItemId] ? (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  Loading...
                                </Typography>
                              ) : relatedItemsMap[item.workItemId] && relatedItemsMap[item.workItemId].length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {relatedItemsMap[item.workItemId].map((relatedItem, idx) => {
                                    const relatedWorkItemId = relatedItem.id || relatedItem.workItemId;
                                    if (!relatedWorkItemId) return null;
                                    
                                    return (
                                      <Link
                                        key={idx}
                                        href={getWorkItemUrl(relatedWorkItemId, relatedItem.teamProject || item.teamProject)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          textDecoration: 'none',
                                          color: '#1976d2',
                                          '&:hover': { textDecoration: 'underline' },
                                          fontSize: '0.875rem'
                                        }}
                                      >
                                        {relatedWorkItemId}
                                      </Link>
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  None
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalItems}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                No Technical Debt items found. Sync items from Azure DevOps to see data here.
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

/**
 * Engineering Processes View Component
 * @returns {JSX.Element} Engineering Processes view
 */
const EngineeringProcessesView = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  return (
    <Box sx={{ width: '100%', maxWidth: '100%', p: 3 }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom sx={{ color: '#1D4434', fontWeight: 'bold' }}>
          Engineering Processes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage engineering processes and practices
        </Typography>
      </Box>
      
      <Paper elevation={0} sx={{ width: '100%', maxWidth: '100%', borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48
              }
            }}
          >
            <Tab label="RCA" />
            <Tab label="Technical Debt" />
            <Tab label="Onboarding" />
          </Tabs>
        </Box>
        
        <Box>
          {activeTab === 0 && <RCATab />}
          {activeTab === 1 && <TechnicalDebtTab />}
          {activeTab === 2 && <OnboardingTab />}
        </Box>
      </Paper>
    </Box>
  );
};

export default EngineeringProcessesView;

